import { Form } from 'factorial-form'

import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type Compensation from 'modules/contracts/store/models/Compensation'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type ContractVersionPayrollPolicy from 'modules/contracts/store/models/ContractVersionPayrollPolicy'

const isEmpty = (array: string[]) => array.length === 0

export const stepHasErrors = (
  form: Form,
  contractVersion: ContractVersion | undefined,
  compensations: Compensation[],
  initialCompensations: Compensation[],
  contractVersionPolicy: ContractVersionPayrollPolicy | null
): boolean => {
  const endDateErrors = getEndDateErrors(form)
  const trialPeriodErrors = getTrialPeriodErrors(form, contractVersion)

  return (
    !isEmpty(endDateErrors) ||
    !isEmpty(trialPeriodErrors) ||
    !canContinue(form, compensations, initialCompensations, contractVersionPolicy)
  )
}

const canContinue = (
  form: Form,
  compensations: Compensation[],
  initialCompensations: Compensation[],
  contractVersionPolicy: ContractVersionPayrollPolicy | null
): boolean => {
  const hasFormChanges = !!form.dirtyFieldsKeys.length
  if (hasFormChanges) return true

  return (
    hasCompensationsChanges(compensations, initialCompensations) ||
    hasPayrollPolicyChange(form, contractVersionPolicy)
  )
}

const hasPayrollPolicyChange = (
  form: Form,
  contractVersionPolicy: ContractVersionPayrollPolicy | null
): boolean => {
  if (!form.has('payroll_policy_id')) return false

  return form.get('payroll_policy_id').value == contractVersionPolicy?.attributes.payroll_policy_id
}

const hasCompensationsChanges = (
  compensations: Compensation[],
  initialCompensations: Compensation[]
): boolean => {
  // Comparing with initial just in case the only change is an AC removal
  if (compensations.length !== initialCompensations.length) return true

  /**
   * Any new compensation won't have an ID assigned.
   * Regardless it is because a new compensation has been created or an update of an already existing compensation.
   */
  const hasNewCompensations = !!compensations.find((compensation) => !compensation.attributes.id)
  if (hasNewCompensations) return true

  return !!compensations.find((compensation) => {
    const sourceCompensation = initialCompensations.find(({ id }) => id === compensation.id)
    if (!sourceCompensation) return true
    const attributes = compensation.attributes
    const sourceAttributes = sourceCompensation.attributes

    return (
      attributes.contract_version_id !== sourceAttributes.contract_version_id ||
      attributes.compensation_type !== sourceAttributes.compensation_type ||
      attributes.description !== sourceAttributes.description ||
      attributes.contracts_taxonomy_id !== sourceAttributes.contracts_taxonomy_id ||
      attributes.calculation !== sourceAttributes.calculation ||
      attributes.amount !== sourceAttributes.amount ||
      attributes.unit !== sourceAttributes.unit ||
      attributes.sync_with_supplements !== sourceAttributes.sync_with_supplements ||
      attributes.recurrence !== sourceAttributes.recurrence ||
      attributes.first_payment_on !== sourceAttributes.first_payment_on ||
      attributes.payroll_policy_id !== sourceAttributes.payroll_policy_id ||
      attributes.recurrence_count !== sourceAttributes.recurrence_count ||
      attributes.starts_on !== sourceAttributes.starts_on
    )
  })
}

const getEndDateErrors = (form: Form): string[] => {
  const errors: string[] = []

  const endDateValue = form.get('ends_on').value
  const effectiveDateValue = form.get('effective_on').value

  if (endDateValue) {
    const endDate = stringToDate(endDateValue, 'ui')
    const effectiveDate = stringToDate(effectiveDateValue, 'ui')
    if (endDate.isBefore(effectiveDate))
      errors.push(
        i18n.t(
          'contract_versions.flows.fix.steps.legal_and_extras.errors.end_date_before_effective'
        )
      )
  }
  form.get('ends_on').setErrors(errors)

  return errors
}

const getTrialPeriodErrors = (
  form: Form,
  contractVersion: ContractVersion | undefined
): string[] => {
  const errors: string[] = []
  if (!form.has('has_trial_period')) return []

  const hasTrial = form.get('has_trial_period').value
  if (hasTrial) {
    const trialEndsOnValue = form.get('trial_period_ends_on').value
    if (!trialEndsOnValue) errors.push(i18n.t('contract_versions.flows.fix.errors.mandatory'))
    const trialEndsOn = stringToDate(trialEndsOnValue, 'ui')

    /**
     * Trial date is carried between versions. Thus, we need to compare with the new start date defined
     * If there is no new start date, current's contract start date should be used.
     * Don't use effective date here if there is a contract version as it would generate false errors.
     */
    const contractStartsOn = contractVersion?.startsOn
    const startsOn =
      form.has('starts_on') && form.get('starts_on').value
        ? stringToDate(form.get('starts_on').value, 'ui')
        : contractStartsOn
        ? contractStartsOn
        : stringToDate(form.get('effective_on').value, 'ui')
    if (startsOn) {
      if (trialEndsOn.isBefore(startsOn)) {
        errors.push(
          i18n.t(
            'contract_versions.flows.fix.steps.legal_and_extras.errors.trial_date_before_effective'
          )
        )
      }
    }
  }

  form.get('trial_period_ends_on').setErrors(errors)
  return errors
}
